import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import tableauLogo from '../../../Assets/tableau_logo.svg';
import { TableauDashboard, TableauSelection } from '../hooks/useFocusMode';

interface TableauPanelProps {
  isOpen: boolean;
  // × / backdrop / Escape / Cancel button — all discard the in-progress
  // pick and close without touching the committed selection.
  onCancel: () => void;
  // Submit button — commits the staged pick (parent closes the modal too).
  onSubmit: (selection: TableauSelection) => void;

  loading: boolean;
  error: string;
  items: TableauDashboard[];
  committedSelection: TableauSelection;

  searchValue: string;
  onSearchChange: (value: string) => void;
}

// Staged pick, local to this modal until Submit.
type Staged =
  | { type: 'none' }
  | { type: 'all' }
  | { type: 'dashboard'; id: string; name: string };

const toStaged = (sel: TableauSelection): Staged => {
  if (sel.type === 'all') return { type: 'all' };
  if (sel.type === 'dashboard' && sel.id) return { type: 'dashboard', id: sel.id, name: sel.name ?? 'Dashboard' };
  return { type: 'none' };
};

const TableauPanel: React.FC<TableauPanelProps> = ({
  isOpen,
  onCancel,
  onSubmit,
  loading,
  error,
  items,
  committedSelection,
  searchValue,
  onSearchChange,
}) => {
  const [staged, setStaged] = useState<Staged>(() => toStaged(committedSelection));

  // TableauPanel itself never unmounts (Modal just renders null while
  // closed), so re-seed the staged pick from whatever's actually committed
  // every time the modal opens — otherwise a discarded (Cancel'd) pick from
  // a prior open would still be sitting in local state next time it opens.
  useEffect(() => {
    if (isOpen) setStaged(toStaged(committedSelection));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const filteredItems = items.filter((item) => {
    if (!searchValue.trim()) return true;
    return item.name.toLowerCase().includes(searchValue.trim().toLowerCase());
  });

  const handleSubmit = () => {
    if (staged.type === 'none') return;
    if (staged.type === 'all') {
      onSubmit({ type: 'all', id: null, name: null });
    } else {
      onSubmit({ type: 'dashboard', id: staged.id, name: staged.name });
    }
  };

  const summaryText =
    staged.type === 'none'      ? 'No selection yet' :
    staged.type === 'all'       ? 'Tableau: Use Tableau Dashboards available' :
    `1 dashboard selected · ${staged.name}`;

  return (
    <Modal isOpen={isOpen} onClose={onCancel} ariaLabel="Select a Tableau dashboard" maxWidth={600}>
      <div className="tableau-modal">
        {/* ── Header ── */}
        <div className="tableau-modal-header">
          <img src={tableauLogo} alt="" className="tableau-modal-header-icon" aria-hidden="true" />
          <div className="tableau-modal-header-text">
            <div className="tableau-modal-title">Select a Tableau dashboard</div>
            <div className="tableau-modal-subtitle">Pick a dashboard to load into this chat, or use all of Tableau</div>
          </div>
          <button
            type="button"
            className="tableau-modal-close"
            onClick={onCancel}
            aria-label="Close"
            title="Close"
          >
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>

        {/* ── Search ── */}
        <div className="tableau-modal-search">
          <i className="fas fa-search" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search dashboards"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        {/* ── Tableau: Use Tableau Dashboards available ── */}
        <button
          type="button"
          className={`tableau-use-all ${staged.type === 'all' ? 'selected' : ''}`}
          onClick={() => setStaged({ type: 'all' })}
        >
          <span className={`tableau-radio ${staged.type === 'all' ? 'checked' : ''}`} aria-hidden="true" />
          <span className="tableau-use-all-text">
            <span className="tableau-use-all-title">Tableau: Use Tableau Dashboards available</span>
            <span className="tableau-use-all-desc">Prompt across every dashboard you have access to — no specific dashboard required</span>
          </span>
        </button>

        <div className="tableau-modal-section-label">OR SELECT A SPECIFIC DASHBOARD</div>

        {/* ── List ── */}
        {error && <div className="focus-error">{error}</div>}
        <div className="tableau-modal-list">
          {loading && <div className="focus-loading"><i className="fas fa-spinner fa-spin" /> Loading…</div>}
          {!loading && filteredItems.length === 0 && (
            <div className="focus-empty">No dashboards available.</div>
          )}
          {!loading && filteredItems.map((item) => {
            const isSelected = staged.type === 'dashboard' && staged.id === item.id;
            return (
              <div key={item.id} className={`tableau-row ${isSelected ? 'selected' : ''}`}>
                <button
                  type="button"
                  className="tableau-row-radio-btn"
                  aria-label={`Select ${item.name}`}
                  onClick={() => setStaged({ type: 'dashboard', id: item.id, name: item.name })}
                >
                  <span className={`tableau-radio ${isSelected ? 'checked' : ''}`} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  className="tableau-row-body"
                  onClick={() => setStaged({ type: 'dashboard', id: item.id, name: item.name })}
                >
                  <img src={tableauLogo} alt="" className="tableau-row-icon" aria-hidden="true" />
                  <span className="tableau-row-text">
                    <span className="tableau-row-name">{item.name}</span>
                  </span>
                </button>
              </div>
            );
          })}
        </div>

        {/* ── Footer ── */}
        <div className="tableau-modal-footer">
          <span className="tableau-footer-summary">{summaryText}</span>
          <div className="tableau-footer-actions">
            <button type="button" className="tableau-btn-cancel" onClick={onCancel}>Cancel</button>
            <button
              type="button"
              className="tableau-btn-submit"
              onClick={handleSubmit}
              disabled={staged.type === 'none'}
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TableauPanel;
